import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAppContext } from '../store/AppContext';
import SafeIcon from '../common/SafeIcon';
import { FiPlus, FiArrowLeft, FiGlobe, FiChevronRight, FiTrash2 } from 'react-icons/fi';
import { motion } from 'framer-motion';

const getFaviconUrl = (url) => {
  try {
    const u = url.startsWith('http') ? url : `https://${url}`;
    const domain = new URL(u).hostname;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return null;
  }
};

const WebsiteCard = ({ web, clientId }) => {
  const [faviconError, setFaviconError] = useState(false);
  const faviconUrl = getFaviconUrl(web.url);

  return (
    <Link to={`/clients/${clientId}/website/${web.id}`}>
      <motion.div
        whileHover={{ y: -2 }}
        className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-indigo-300 hover:shadow-md transition-all flex items-center justify-between group"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shadow-sm flex-shrink-0">
            {faviconUrl && !faviconError ? (
              <img
                src={faviconUrl}
                alt=""
                className="w-8 h-8 object-contain"
                onError={() => setFaviconError(true)}
              />
            ) : (
              <SafeIcon icon={FiGlobe} className="text-xl text-slate-400" />
            )}
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-800 group-hover:text-indigo-700 transition-colors">
              {web.url.replace(/https?:\/\//, '')}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              {web.keywords?.length || 0} keywords •{' '}
              {web.keywords?.filter(k => k.isTargeted)?.length || 0} targetate •{' '}
              {web.advertorials?.length || 0} advertoriale
            </p>
          </div>
        </div>
        <SafeIcon icon={FiChevronRight} className="text-slate-300 group-hover:text-indigo-500 text-xl transition-colors" />
      </motion.div>
    </Link>
  );
};

const ClientDetail = () => {
  const { id } = useParams();
  const { data, setData, t } = useAppContext();
  const [isAdding, setIsAdding] = useState(false);
  const [newUrl, setNewUrl] = useState('');

  const client = data.clients.find(c => c.id === id);
  if (!client) return <div>Client not found</div>;

  const handleAdd = (e) => {
    e.preventDefault();
    if (!newUrl.trim()) return;
    const newWeb = { id: `w_${Date.now()}`, url: newUrl, keywords: [], advertorials: [] };
    setData(prev => {
      const newClients = prev.clients.map(c => {
        if (c.id === id) return { ...c, websites: [...(c.websites || []), newWeb] };
        return c;
      });
      return { ...prev, clients: newClients };
    });
    setNewUrl('');
    setIsAdding(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/clients" className="p-2 hover:bg-slate-200 rounded-lg text-slate-500 transition-colors">
          <SafeIcon icon={FiArrowLeft} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{client.name}</h1>
          <p className="text-sm text-slate-500">{client.websites?.length || 0} website-uri</p>
        </div>
      </div>

      <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-700">{t('websites')}</h2>
        <button
          onClick={() => setIsAdding(!isAdding)}
          className="flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg hover:bg-indigo-100 transition-colors font-medium"
        >
          <SafeIcon icon={FiPlus} /> {t('addWebsite')}
        </button>
      </div>

      {isAdding && (
        <motion.form
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleAdd}
          className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex gap-4 items-end"
        >
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 mb-1">{t('websiteUrl')}</label>
            <input
              type="text"
              placeholder="https://example.com"
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 outline-none"
              autoFocus
            />
          </div>
          <button type="submit" className="bg-slate-800 text-white px-6 py-2 rounded-lg hover:bg-slate-900 transition-colors">{t('save')}</button>
          <button type="button" onClick={() => setIsAdding(false)} className="bg-slate-100 text-slate-600 px-6 py-2 rounded-lg hover:bg-slate-200 transition-colors">{t('cancel')}</button>
        </motion.form>
      )}

      <div className="grid grid-cols-1 gap-4">
        {(client.websites || []).map(web => (
          <WebsiteCard key={web.id} web={web} clientId={id} />
        ))}
        {!(client.websites?.length) && !isAdding && (
          <div className="py-12 text-center text-slate-500 bg-white rounded-xl border border-dashed border-slate-300">
            {t('noData')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDetail;